/**
 * Vimeo Modal Player
 * Displays Vimeo videos in a lightbox modal overlay
 *
 * Usage:
 * Add to any element:
 * - data-vimeo-trigger: Marks the clickable element
 * - data-vimeo-id: Numeric video ID, or a full Vimeo URL (e.g. "1096306723" or "https://vimeo.com/1096306723")
 *
 * Optional attributes:
 * - data-vimeo-autoplay: "true" or "false" (default: "true")
 * - data-vimeo-mute: "true" or "false" (default: "true")
 * - data-vimeo-bg-color: Background color (default: "rgba(0, 0, 0, 0.9)")
 *
 * Example:
 * <div data-vimeo-trigger data-vimeo-id="https://vimeo.com/1096306723">
 *   <img src="thumbnail.jpg" alt="Play video" />
 * </div>
 */

export type VimeoParsedRef = { id: string; privacyHash?: string };

/** Accepts a numeric id, vimeo.com URL, or player.vimeo.com URL (including optional ?h= for privacy hash). */
export function parseVimeoRef(raw: string): VimeoParsedRef | null {
  const s = raw.trim();
  if (!s) return null;

  if (/^\d+$/.test(s)) {
    return { id: s };
  }

  try {
    const href = s.includes('://') ? s : `https://${s}`;
    const u = new URL(href);

    if (!u.hostname.endsWith('vimeo.com')) {
      return null;
    }

    const pathTrim = u.pathname.replace(/\/$/, '');

    const privateMatch = pathTrim.match(/\/(\d+)\/([a-f0-9]{6,})\/?$/i);
    if (privateMatch) {
      return { id: privateMatch[1], privacyHash: privateMatch[2] };
    }

    const playerMatch = pathTrim.match(/\/video\/(\d+)/i);
    if (playerMatch) {
      const id = playerMatch[1];
      const privacyHash = u.searchParams.get('h') ?? undefined;
      return privacyHash ? { id, privacyHash } : { id };
    }

    const parts = pathTrim.split('/').filter(Boolean);
    const numericParts = parts.filter((p) => /^\d+$/.test(p));

    if (numericParts.length === 0) {
      return null;
    }

    return { id: numericParts[numericParts.length - 1] };
  } catch {
    return null;
  }
}

export class VimeoModal {
  private wrapper: HTMLElement | null = null;
  private videoContainer: HTMLElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private previouslyFocused: HTMLElement | null = null;
  private videoRef: VimeoParsedRef;
  private autoplay: boolean;
  private mute: boolean;
  private backgroundColor: string;

  constructor(
    videoRef: VimeoParsedRef,
    options: {
      autoplay?: boolean;
      mute?: boolean;
      backgroundColor?: string;
    } = {}
  ) {
    this.videoRef = videoRef;
    this.autoplay = options.autoplay ?? true;
    this.mute = options.mute ?? true;
    this.backgroundColor = options.backgroundColor ?? 'rgba(0, 0, 0, 0.9)';
    this.previouslyFocused = document.activeElement as HTMLElement;
  }

  public open(): void {
    this.createWrapper();
    this.createVideoContainer();
    this.createVideo();
    this.createCloseButton();
    this.setupEventListeners();
    this.appendToDOM();
    this.setInitialFocus();
  }

  private createWrapper(): void {
    this.wrapper = document.createElement('div');
    Object.assign(this.wrapper.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '9999',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: this.backgroundColor,
    });

    this.wrapper.setAttribute('role', 'dialog');
    this.wrapper.setAttribute('aria-label', 'Vimeo video player');
    this.wrapper.setAttribute('data-vimeo-modal', 'wrapper');
  }

  private createVideoContainer(): void {
    this.videoContainer = document.createElement('div');
    this.videoContainer.setAttribute('data-vimeo-modal', 'container');

    Object.assign(this.videoContainer.style, {
      position: 'relative',
      width: '90vw',
      maxWidth: '1200px',
      aspectRatio: '16/9',
      background: '#000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
      overflow: 'hidden',
    });
  }

  private createVideo(): void {
    this.iframe = document.createElement('iframe');
    this.iframe.setAttribute('data-vimeo-modal', 'iframe');
    Object.assign(this.iframe.style, {
      width: '100%',
      height: '100%',
      border: 'none',
      borderRadius: '4px',
      position: 'absolute',
      top: '0',
      left: '0',
    });

    const params = new URLSearchParams();
    if (this.videoRef.privacyHash) {
      params.set('h', this.videoRef.privacyHash);
    }
    if (this.autoplay) {
      params.set('autoplay', '1');
    }
    if (this.mute) {
      params.set('muted', '1');
    }
    params.set('playsinline', '1');

    const query = params.toString();
    this.iframe.src = `https://player.vimeo.com/video/${this.videoRef.id}${query ? `?${query}` : ''}`;
    this.iframe.allow = 'autoplay; fullscreen; picture-in-picture; encrypted-media';
    this.iframe.allowFullscreen = true;
    this.iframe.setAttribute('title', 'Vimeo video player');

    if (this.videoContainer && this.iframe) {
      this.videoContainer.appendChild(this.iframe);
    }
    if (this.wrapper && this.videoContainer) {
      this.wrapper.appendChild(this.videoContainer);
    }
  }

  private createCloseButton(): void {
    this.closeButton = document.createElement('button');
    this.closeButton.setAttribute('data-vimeo-modal', 'close');
    Object.assign(this.closeButton.style, {
      position: 'absolute',
      top: '48px',
      right: '22px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '0',
      zIndex: '10000',
    });
    this.closeButton.setAttribute('aria-label', 'Close video player');
    this.closeButton.innerHTML = `<svg width="18" height="17" viewBox="0 0 18 17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_vimeo_modal)">
        <path opacity="0.4" d="M3.99996 4.03523e-05L8.94971 4.94979L13.8995 4.03523e-05L17.435 3.53557L12.4852 8.48532L17.435 13.4351L13.8995 16.9706L8.94971 12.0209L3.99996 16.9706L0.464426 13.4351L5.41417 8.48532L0.464426 3.53557L3.99996 4.03523e-05Z" fill="black"/>
        <path d="M3.99996 1.41435L8.94971 6.36409L13.8995 1.41435L16.0208 3.53567L11.071 8.48541L16.0208 13.4352L13.8995 15.5565L8.94971 10.6067L3.99996 15.5565L1.87864 13.4352L6.82839 8.48541L1.87864 3.53567L3.99996 1.41435Z" fill="white"/>
      </g>
      <defs>
        <clipPath id="clip0_vimeo_modal">
          <rect width="18" height="17" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;

    this.closeButton.onclick = () => this.cleanup();
    this.wrapper?.appendChild(this.closeButton);
  }

  private setupEventListeners(): void {
    if (!this.wrapper) return;

    this.wrapper.addEventListener('click', (e) => {
      if (e.target === this.wrapper) this.cleanup();
    });

    document.addEventListener('keydown', this.handleEscape);
    document.addEventListener('keydown', this.handleTab);
  }

  private appendToDOM(): void {
    if (!this.wrapper) return;
    document.body.appendChild(this.wrapper);
  }

  private setInitialFocus(): void {
    this.iframe?.focus();
  }

  private handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') this.cleanup();
  };

  private handleTab = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      this.iframe?.focus();
    }
  };

  public cleanup(): void {
    if (this.wrapper) {
      this.wrapper.remove();
    }

    document.removeEventListener('keydown', this.handleEscape);
    document.removeEventListener('keydown', this.handleTab);

    this.previouslyFocused?.focus();
  }
}

/**
 * Vimeo Modal Controller
 * Automatically initializes all Vimeo triggers on the page
 */
export class VimeoModalController {
  private triggers: NodeListOf<HTMLElement> | null = null;

  public init(): void {
    this.triggers = document.querySelectorAll('[data-vimeo-trigger]');

    if (!this.triggers || this.triggers.length === 0) {
      return;
    }

    this.triggers.forEach((trigger) => {
      this.setupTrigger(trigger);
    });
  }

  private setupTrigger(trigger: HTMLElement): void {
    const raw = trigger.getAttribute('data-vimeo-id');

    if (!raw) {
      console.error('Vimeo trigger missing data-vimeo-id attribute:', trigger);
      return;
    }

    const videoRef = parseVimeoRef(raw);
    if (!videoRef) {
      console.error(
        'Vimeo trigger has invalid data-vimeo-id (expected id or vimeo.com URL):',
        raw,
        trigger
      );
      return;
    }

    const autoplay = trigger.getAttribute('data-vimeo-autoplay') !== 'false';
    const mute = trigger.getAttribute('data-vimeo-mute') !== 'false';
    const backgroundColor = trigger.getAttribute('data-vimeo-bg-color') || 'rgba(0, 0, 0, 0.9)';

    const openModal = (e: Event) => {
      e.preventDefault();
      const modal = new VimeoModal(videoRef, {
        autoplay,
        mute,
        backgroundColor,
      });
      modal.open();
    };

    trigger.addEventListener('click', openModal);

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        openModal(e);
      }
    });

    if (!trigger.hasAttribute('tabindex')) {
      trigger.setAttribute('tabindex', '0');
    }

    if (trigger.tagName !== 'BUTTON' && trigger.tagName !== 'A' && !trigger.hasAttribute('role')) {
      trigger.setAttribute('role', 'button');
    }

    trigger.style.cursor = 'pointer';
  }
}
